import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, addWeeks, isSameMonth, isSameDay, parseISO, startOfDay, setYear, setMonth, setDate } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Badge } from '@/components/ui/badge';

const statusColors = {
  PREVU: 'bg-blue-100 text-blue-800 border-blue-300',
  REALISE: 'bg-green-100 text-green-800 border-green-300',
  ANNULE: 'bg-red-100 text-red-800 border-red-300',
  NO_SHOW: 'bg-gray-100 text-gray-800 border-gray-300',
};

const dotColors = {
  PREVU: 'bg-blue-500',
  REALISE: 'bg-green-500',
  ANNULE: 'bg-red-500',
  NO_SHOW: 'bg-gray-400',
};

export default function AppointmentCalendar({ appointments, clients, services, onAppointmentClick, onAppointmentDrop, view = 'month' }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [draggedApt, setDraggedApt] = useState(null);
  const [dragOverDay, setDragOverDay] = useState(null);

  // Drag & drop handlers
  const handleDragStart = (e, apt) => {
    setDraggedApt(apt);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedApt(null);
    setDragOverDay(null);
  };

  const handleDragOver = (e, day) => {
    if (!onAppointmentDrop) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!dragOverDay || !isSameDay(dragOverDay, day)) {
      setDragOverDay(day);
    }
  };

  const handleDrop = (e, day) => {
    if (!onAppointmentDrop || !draggedApt) return;
    e.preventDefault();
    setDragOverDay(null);
    // Si même jour, ne rien faire
    if (isSameDay(draggedApt.dateObj, day)) {
      setDraggedApt(null);
      return;
    }
    // Conserver l'heure d'origine, changer la date
    let newDate = setYear(draggedApt.dateObj, day.getFullYear());
    newDate = setMonth(newDate, day.getMonth());
    newDate = setDate(newDate, day.getDate());
    const newDateStr = format(newDate, "yyyy-MM-dd'T'HH:mm");
    onAppointmentDrop(draggedApt, newDateStr);
    setDraggedApt(null);
  };

  // Navigation
  const goToPrevious = () => {
    if (view === 'month') {
      setCurrentDate(addMonths(currentDate, -1));
    } else {
      setCurrentDate(addWeeks(currentDate, -1));
    }
  };

  const goToNext = () => {
    if (view === 'month') {
      setCurrentDate(addMonths(currentDate, 1));
    } else {
      setCurrentDate(addWeeks(currentDate, 1));
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Enrichir les RDV avec infos client/service
  const enrichedAppointments = useMemo(() => {
    return appointments.map(apt => {
      const client = clients.find(c => c.id === apt.client_id);
      const service = services.find(s => s.id === apt.service_id);
      return {
        ...apt,
        clientName: client ? `${client.prenom} ${client.nom}` : 'Inconnue',
        serviceName: service ? service.nom : 'Service inconnu',
        dateObj: parseISO(apt.date)
      };
    });
  }, [appointments, clients, services]);

  // Vue Mois
  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { locale: fr });
    const endDate = endOfWeek(monthEnd, { locale: fr });

    const dateFormat = "d";
    const rows = [];
    let days = [];
    let day = startDate;

    // Header jours de la semaine
    const daysHeader = [];
    const daysOfWeek = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
    daysOfWeek.forEach(dayName => {
      daysHeader.push(
        <div key={dayName} className="text-center font-semibold text-[11px] sm:text-sm text-muted-foreground p-1 sm:p-2">
          <span className="sm:hidden">{dayName.charAt(0)}</span>
          <span className="hidden sm:inline">{dayName}</span>
        </div>
      );
    });

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = day;
        const dayAppointments = enrichedAppointments.filter(apt => 
          isSameDay(apt.dateObj, cloneDay)
        );

        days.push(
          <div
            key={day}
            onDragOver={(e) => handleDragOver(e, cloneDay)}
            onDrop={(e) => handleDrop(e, cloneDay)}
            className={`min-h-[52px] sm:min-h-[100px] border border-border p-1 sm:p-2 ${
              !isSameMonth(day, monthStart) ? 'bg-muted/30 text-muted-foreground' : 'bg-background'
            } ${isSameDay(day, new Date()) ? 'ring-2 ring-primary' : ''} ${
              dragOverDay && isSameDay(dragOverDay, cloneDay) ? 'bg-primary/10 ring-2 ring-primary/50' : ''
            }`}
          >
            <div className="font-medium text-xs sm:text-sm mb-0.5 sm:mb-1">{format(cloneDay, dateFormat)}</div>
            {/* Vue détaillée (tablette/desktop) */}
            <div className="hidden sm:block space-y-1">
              {dayAppointments.slice(0, 3).map(apt => (
                <div
                  key={apt.id}
                  draggable={!!onAppointmentDrop}
                  onDragStart={(e) => handleDragStart(e, apt)}
                  onDragEnd={handleDragEnd}
                  onClick={() => onAppointmentClick(apt)}
                  className={`text-xs p-1 rounded cursor-pointer border ${statusColors[apt.statut]} hover:opacity-80 ${
                    draggedApt && draggedApt.id === apt.id ? 'opacity-40' : ''
                  }`}
                >
                  <div className="font-semibold truncate">{format(apt.dateObj, 'HH:mm')}</div>
                  <div className="truncate">{apt.clientName}</div>
                </div>
              ))}
              {dayAppointments.length > 3 && (
                <div className="text-xs text-muted-foreground">+{dayAppointments.length - 3} autre(s)</div>
              )}
            </div>
            {/* Vue compacte (mobile) : pastilles cliquables */}
            <div className="flex sm:hidden flex-wrap gap-1 mt-0.5">
              {dayAppointments.slice(0, 4).map(apt => (
                <button
                  key={apt.id}
                  type="button"
                  onClick={() => onAppointmentClick(apt)}
                  aria-label={`${format(apt.dateObj, 'HH:mm')} ${apt.clientName}`}
                  className={`w-2 h-2 rounded-full ${dotColors[apt.statut] || 'bg-gray-400'}`}
                />
              ))}
              {dayAppointments.length > 4 && (
                <span className="text-[9px] leading-none text-muted-foreground">+{dayAppointments.length - 4}</span>
              )}
            </div>
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div key={day} className="grid grid-cols-7">
          {days}
        </div>
      );
      days = [];
    }

    return (
      <div>
        <div className="grid grid-cols-7 mb-2">
          {daysHeader}
        </div>
        <div className="space-y-0">
          {rows}
        </div>
      </div>
    );
  };

  // Vue Semaine
  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate, { locale: fr });
    const weekDays = [];
    
    for (let i = 0; i < 7; i++) {
      const day = addDays(weekStart, i);
      const dayAppointments = enrichedAppointments.filter(apt => 
        isSameDay(apt.dateObj, day)
      ).sort((a, b) => a.dateObj - b.dateObj);

      weekDays.push(
        <div
          key={i}
          onDragOver={(e) => handleDragOver(e, day)}
          onDrop={(e) => handleDrop(e, day)}
          className={`border-r border-border last:border-r-0 min-h-[300px] sm:min-h-[500px] ${
            dragOverDay && isSameDay(dragOverDay, day) ? 'bg-primary/10' : ''
          }`}
        >
          <div className={`p-2 sm:p-3 border-b border-border font-semibold text-center ${
            isSameDay(day, new Date()) ? 'bg-primary text-primary-foreground' : 'bg-muted'
          }`}>
            <div className="text-xs sm:text-sm capitalize">{format(day, 'EEE', { locale: fr })}</div>
            <div className="text-lg sm:text-2xl">{format(day, 'd')}</div>
          </div>
          <div className="p-1.5 sm:p-2 space-y-2">
            {dayAppointments.map(apt => (
              <div
                key={apt.id}
                draggable={!!onAppointmentDrop}
                onDragStart={(e) => handleDragStart(e, apt)}
                onDragEnd={handleDragEnd}
                onClick={() => onAppointmentClick(apt)}
                className={`p-2 rounded border ${statusColors[apt.statut]} cursor-pointer hover:opacity-80 ${
                  draggedApt && draggedApt.id === apt.id ? 'opacity-40' : ''
                }`}
              >
                <div className="font-bold text-xs sm:text-sm">{format(apt.dateObj, 'HH:mm')}</div>
                <div className="font-medium text-xs sm:text-sm truncate">{apt.clientName}</div>
                <div className="text-[11px] sm:text-xs truncate">{apt.serviceName}</div>
                <div className="text-[11px] sm:text-xs mt-1">{apt.prix}€</div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto -mx-2 px-2 sm:mx-0 sm:px-0">
        <div className="grid grid-cols-7 border border-border rounded-lg overflow-hidden min-w-[640px]">
          {weekDays}
        </div>
      </div>
    );
  };

  return (
    <Card className="rounded-xl border-border/60">
      <CardHeader className="p-3 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle className="text-xl sm:text-2xl capitalize">
              {format(currentDate, view === 'month' ? 'MMMM yyyy' : "'Semaine du' d MMMM yyyy", { locale: fr })}
            </CardTitle>
            {onAppointmentDrop && (
              <p className="hidden sm:block text-xs text-muted-foreground mt-1">Glissez-déposez un rendez-vous pour le reprogrammer</p>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={goToToday}>
              Aujourd'hui
            </Button>
            <Button variant="outline" size="icon" onClick={goToPrevious}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={goToNext}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-2 sm:p-6">
        {view === 'month' ? renderMonthView() : renderWeekView()}
      </CardContent>
    </Card>
  );
}
